import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      }
      if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      if (attempts < 5) {
        setTimeout(() => setAttempts(a => a + 1), 2000);
      } else {
        setStatus('timeout');
      }
    } catch (err) {
      setStatus('error');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollStatus();
  }, [sessionId, pollStatus]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 pb-16 flex items-center justify-center" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center px-4 max-w-md"
      >
        {status === 'checking' && (
          <>
            <div className="w-12 h-12 border-2 border-[#C4A35A] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#E8D5A3] mb-3">Processing Payment</h1>
            <p className="text-sm text-[#9A8E7A]">Please wait while we confirm your order...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C4A35A]/10 flex items-center justify-center border border-[#C4A35A]/20">
              <CheckCircle size={32} className="text-[#C4A35A]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#E8D5A3] mb-3" data-testid="success-title">Thank You!</h1>
            <p className="text-sm text-[#9A8E7A] mb-8 leading-relaxed">
              Your order has been confirmed. We're getting your products ready with love and care.
            </p>
            <Button asChild className="bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full px-8 h-12 font-bold" data-testid="continue-shopping-success">
              <Link to="/shop">Continue Shopping</Link>
            </Button>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#E8D5A3] mb-3">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-sm text-[#9A8E7A] mb-8">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t confirm your payment. Please check your email for confirmation.'}
            </p>
            <Button asChild className="bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full px-8 h-12 font-bold" data-testid="back-to-shop-btn">
              <Link to="/shop">Back to Shop</Link>
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
